import { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import CalculatorPanel from "@/components/CalculatorPanel";
import ResultBreakdown from "@/components/ResultBreakdown";
import HistorySection from "@/components/HistorySection";
import PrintReport from "@/components/PrintReport";
import { api } from "@/lib/api";
import { useConfig } from "@/context/ConfigContext";
import { calculateFeed, buildShareText, computeCost, normalizeModel, EMPTY_PRICES, fmt } from "@/lib/feedModels";

export default function FeedCalculator({ withCost = false }) {
  const { config } = useConfig();
  const models = (config.feed_models || []).map(normalizeModel);
  const zones = config.zones || [];
  const [totalWeight, setTotalWeight] = useState("");
  const [modelId, setModelId] = useState("");
  const [name, setName] = useState("");
  const [zoneSel, setZoneSel] = useState("");
  const [zoneSummary, setZoneSummary] = useState([]);
  const [prices, setPrices] = useState(EMPTY_PRICES);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [saving, setSaving] = useState(false);
  const [printCalc, setPrintCalc] = useState(null);

  useEffect(() => {
    if (!modelId && models.length) setModelId(models[0].id);
  }, [models, modelId]);

  useEffect(() => {
    api.get("/cattle/zone-summary").then((r) => setZoneSummary(r.data)).catch(() => {});
  }, []);

  const zoneStats = useMemo(() => {
    if (!zoneSel) return null;
    const s = zoneSummary.find((z) => z.zone === zoneSel);
    return s ? { count: s.count, total: s.total_weight } : { count: 0, total: 0 };
  }, [zoneSel, zoneSummary]);

  const handleZoneSelect = (zone) => {
    setZoneSel(zone);
    const s = zoneSummary.find((z) => z.zone === zone);
    const total = s ? s.total_weight : 0;
    setTotalWeight(total ? String(total) : "");
    if (!name.trim()) setName(zone);
  };

  const selectedModel = models.find((m) => m.id === modelId) || null;
  const result = useMemo(
    () => calculateFeed(totalWeight, selectedModel),
    [totalWeight, selectedModel]
  );
  const cost = useMemo(() => computeCost(result, prices), [result, prices]);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await api.get("/calculations");
      setHistory(res.data);
    } catch (e) {
      console.error(e);
      toast.error("Gagal memuat riwayat perhitungan");
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const handleSave = async () => {
    if (!result) return;
    setSaving(true);
    try {
      const res = await api.post("/calculations", {
        total_weight: result.totalWeight,
        feed_model: modelId,
        name: name.trim() || null,
        prices: withCost ? prices : {},
      });
      setHistory((prev) => [res.data, ...prev]);
      toast.success("Perhitungan berhasil disimpan");
    } catch (e) {
      console.error(e);
      toast.error("Gagal menyimpan perhitungan");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/calculations/${id}`);
      setHistory((prev) => prev.filter((item) => item.id !== id));
      toast.success("Riwayat berhasil dihapus");
    } catch (e) {
      console.error(e);
      toast.error("Gagal menghapus riwayat");
    }
  };

  const handlePrint = useCallback((calc) => {
    setPrintCalc(calc);
    setTimeout(() => window.print(), 150);
  }, []);

  const handleCopy = async () => {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(
        buildShareText(result, name.trim(), withCost ? cost : null)
      );
      toast.success("Rincian pakan disalin ke clipboard");
    } catch (e) {
      console.error(e);
      toast.error("Gagal menyalin rincian");
    }
  };

  const handleReset = () => {
    setTotalWeight("");
    setModelId(models[0]?.id || "");
    setName("");
    setZoneSel("");
    setPrices(EMPTY_PRICES);
  };

  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <CalculatorPanel
          totalWeight={totalWeight}
          setTotalWeight={setTotalWeight}
          modelId={modelId}
          setModelId={setModelId}
          models={models}
          name={name}
          setName={setName}
          zones={zones}
          zoneSel={zoneSel}
          onZoneSelect={handleZoneSelect}
          zoneStats={zoneStats}
          result={result}
          onSave={handleSave}
          onReset={handleReset}
          saving={saving}
        />
        <ResultBreakdown
          result={result}
          cost={cost}
          prices={prices}
          setPrices={setPrices}
          showCost={withCost}
          onPrint={() =>
            handlePrint({ ...result, name: name.trim() || null, cost: withCost ? cost : null })
          }
          onCopy={handleCopy}
        />
        <HistorySection
          history={history}
          loading={loadingHistory}
          onPrint={handlePrint}
          onDelete={handleDelete}
          showCost={withCost}
        />
      </div>
      <PrintReport calc={printCalc} />
    </>
  );
}
